/**
 * المرحلة 1.35 — the quick card, before the server writes it: its lines
 * previewed from the price list's leaves, what still stops it, the body it
 * sends, and the paper it prints.
 *
 * A preview, not the price: the server prices every line again from the list
 * (Backend/routes/quickDesk.js) and the paper is drawn from what it saved.
 * Pure; Tests/suites/frontend/p135-center.test.mjs.
 */

import { amountWords, boxesOf, split } from './amountWords';
import { MIN_REASON, applyDiscounts } from './discounts';
import { balanceOf as refundBalance } from './refund';

/** The relation chips under «وصل من السيد/ة». */
export const RELATIONS = Object.freeze(['father', 'mother', 'guardian']);
/** The one identifier: a mobile (the guardian's for a child), a national ID, an insurance number. */
export const ID_KINDS = Object.freeze(['mobile', 'nationalId', 'insurance']);

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const asciiDigits = (v) => String(v ?? '').replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

/** Does a line of this unit take a count on the card (days, hours, sessions, pieces)? */
export const counts = (unit) => Boolean(unit) && unit !== 'once';

/** How many names were typed. */
export const nameCount = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).length;

/** An empty card, as the policy starts it. */
export function emptyCard({ minorDefault = 'no' } = {}) {
  return {
    fileId: null,
    found: null,
    isMinor: minorDefault === 'yes',
    fullName: '',
    guardianName: '',
    guardianRelation: 'father',
    idKind: 'mobile',
    idValue: '',
    lines: [],
    receiptType: 'cash',
    paidAmount: '',
    // A named discount on the total, and why (lib/priceDiscounts.js).
    discount: { discountId: '', reason: '' },
    extra: {},
  };
}

/** A line for a leaf, or one more of it when the card has it already. */
export function addLine(lines, leaf) {
  const list = Array.isArray(lines) ? lines : [];
  const at = list.findIndex((l) => l.leafId === leaf.id);
  if (at >= 0 && counts(leaf.unit)) return list.map((l, i) => (i === at ? { ...l, quantity: Number(l.quantity || 1) + 1 } : l));
  if (at >= 0) return list;
  return [...list, { key: `${leaf.id}-${list.length}`, leafId: leaf.id, quantity: 1, discountId: '', discountReason: '' }];
}

/**
 * The card's lines priced from the leaves, for the preview: each with its
 * leaf, quantity and amount, and the total. A line whose leaf is gone (hidden
 * since the card was opened) is dropped.
 */
export function previewLines(leaves, lines) {
  const byId = new Map((Array.isArray(leaves) ? leaves : []).map((l) => [l.id, l]));
  const out = [];
  for (const line of Array.isArray(lines) ? lines : []) {
    const leaf = byId.get(line.leafId);
    if (!leaf) continue;
    const quantity = counts(leaf.unit) ? Math.max(0, round2(asciiDigits(line.quantity))) : 1;
    out.push({ ...line, leaf, quantity, amount: round2(leaf.price * quantity) });
  }
  return { lines: out, total: round2(out.reduce((s, l) => s + l.amount, 0)) };
}

/**
 * The card priced as the server will price it: the lines from the leaves,
 * then the named discounts the desk picked — on a line and on the total —
 * before the reasons are typed. `total` is what the voucher will say.
 */
export function pricedCard(leaves, card, discounts = []) {
  const preview = previewLines(leaves, card ? card.lines : []);
  const applied = applyDiscounts({
    lines: preview.lines.map((l) => ({ priceItemId: l.leafId, amount: l.amount, label: l.leaf.path.join(' — ') })),
    asked: preview.lines.map((l) => ({ discountId: l.discountId, discountReason: l.discountReason })),
    invoice: card && card.discount ? card.discount : null,
    discounts,
    leaves,
    reasons: false,
  });
  if (applied.error) return { lines: preview.lines.map((l) => ({ ...l, gross: l.amount, off: 0 })), subtotal: preview.total, discountTotal: 0, invoice: null, total: preview.total, error: applied.error };
  return {
    lines: preview.lines.map((l, i) => ({ ...l, gross: l.amount, off: applied.lines[i].discount ? applied.lines[i].discount.amount : 0, amount: applied.lines[i].amount })),
    subtotal: applied.subtotal,
    discountTotal: applied.discountTotal,
    invoice: applied.invoice,
    total: applied.total,
  };
}

/** «مبلغ وقدره» and the two boxes. */
export function wordsFor(total, currency = 'EGP') {
  const { major, minor } = split(total);
  return { words: amountWords(total, { currency }), major, minor, boxes: boxesOf(currency) };
}

/** The paid-now figure and what is left, for «آجل». */
export function balanceOf(card, total) {
  if (card.receiptType !== 'deferred') return { paid: total, remaining: 0 };
  const paid = Math.min(total, Math.max(0, round2(asciiDigits(card.paidAmount))));
  return { paid, remaining: round2(total - paid) };
}

/**
 * What still stops the card: 'name', 'nameParts', 'guardian', 'identifier',
 * 'lines', 'quantity', 'discountReason'. Empty means it can be issued.
 */
export function missing(card, { nameParts = 4, leaves = [] } = {}) {
  const out = [];
  if (!card.fileId) {
    if (!String(card.fullName || '').trim()) out.push('name');
    else if (nameCount(card.fullName) < nameParts) out.push('nameParts');
    if (card.isMinor && !String(card.guardianName || '').trim()) out.push('guardian');
    const digits = asciiDigits(card.idValue).replace(/\D/g, '');
    if (card.idKind === 'mobile' ? digits.length < 7 : !String(card.idValue || '').trim()) out.push('identifier');
  }
  const { lines } = previewLines(leaves, card.lines);
  if (!lines.length) out.push('lines');
  else if (lines.some((l) => !(l.quantity > 0))) out.push('quantity');
  // Every discount says why, every time.
  const unexplained = (id, reason) => Boolean(id) && String(reason || '').trim().length < MIN_REASON;
  if (lines.some((l) => unexplained(l.discountId, l.discountReason)) || (card.discount && unexplained(card.discount.discountId, card.discount.reason))) out.push('discountReason');
  return out;
}

/** The body for POST /api/quick-desk/issue. Prices are not sent: the server reads them. */
export function payloadOf(card, leaves = []) {
  const { lines } = previewLines(leaves, card.lines);
  const patient = card.fileId
    ? { id: card.fileId }
    : {
        fullName: String(card.fullName || '').trim(),
        isMinor: Boolean(card.isMinor),
        guardianName: card.isMinor ? String(card.guardianName || '').trim() || null : null,
        guardianRelation: card.isMinor ? card.guardianRelation : null,
        identifier: { kind: card.idKind, value: asciiDigits(card.idValue).trim() },
        extra: Object.fromEntries(Object.entries(card.extra || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined)),
      };
  const payerName = card.isMinor ? String(card.guardianName || '').trim() : String(card.fullName || '').trim();
  return {
    patient,
    payerName: payerName || undefined,
    payerRelation: card.isMinor ? card.guardianRelation : 'self',
    lines: lines.map((l) =>
      l.discountId
        ? { priceItemId: l.leafId, quantity: l.quantity, discountId: Number(l.discountId), discountReason: String(l.discountReason || '').trim() }
        : { priceItemId: l.leafId, quantity: l.quantity },
    ),
    discount: card.discount && card.discount.discountId ? { discountId: Number(card.discount.discountId), reason: String(card.discount.reason || '').trim() } : undefined,
    receiptType: card.receiptType,
    paidAmount: card.receiptType === 'deferred' ? asciiDigits(card.paidAmount || '0') : undefined,
  };
}

/**
 * The paper's data from a saved receipt, in the shape the public page gets
 * (Backend/lib/voucher.js publicVoucher), so one sheet draws both.
 */
export function sheetOf(receipt, { institution = {}, currency = 'EGP' } = {}) {
  const r = receipt || {};
  const patient = r.patient || {};
  const total = round2(r.total);
  const paid = round2(r.paidAmount);
  return {
    number: r.number,
    serial: r.serial,
    title: r.title || 'إيصال تحصيل نقدية',
    issuedAt: r.issuedAt,
    status: r.status,
    receiptType: r.receiptType,
    payerName: r.payerName || null,
    payerRelation: r.payerRelation || null,
    patientName: patient.fullName || null,
    isMinor: Boolean(patient.isMinor),
    mobile: patient.phone1 || null,
    items: (Array.isArray(r.items) ? r.items : []).map((l) => ({
      label: l.label,
      path: Array.isArray(l.path) ? l.path : null,
      quantity: l.quantity === undefined ? 1 : l.quantity,
      unit: l.unit || 'once',
      unitPrice: l.unitPrice === undefined ? l.amount : l.unitPrice,
      amount: round2(l.amount),
      ...(l.discount ? { gross: round2(l.gross), discount: { name: l.discount.name, kind: l.discount.kind, value: l.discount.value, amount: round2(l.discount.amount) } } : {}),
    })),
    discount: r.discount
      ? {
          subtotal: round2(r.discount.subtotal),
          total: round2(r.discount.total),
          invoice: r.discount.invoice ? { name: r.discount.invoice.name, kind: r.discount.invoice.kind, value: r.discount.invoice.value, amount: round2(r.discount.invoice.amount) } : null,
        }
      : null,
    refunds: (Array.isArray(r.refunds) ? r.refunds : []).map((x) => ({ at: x.at, amount: round2(x.amount), lines: (x.lines || []).map((l) => ({ label: l.label, quantity: l.quantity, unit: l.unit, amount: round2(l.amount) })) })),
    total,
    paidAmount: paid,
    remaining: refundBalance(r).due,
    ...wordsFor(total, currency),
    currency,
    signatureName: r.signatureName,
    signatureTitle: r.signatureTitle || null,
    institution,
  };
}
